import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlineDownload, HiOutlineChevronDown, HiOutlineChevronUp, HiOutlineEye } from 'react-icons/hi';

const SALES_ASSET_TYPES = {
  sales_proposal: { label: 'Proposal', format: 'DOCX', description: 'A tailored business proposal for your prospect' },
  sales_deck: { label: 'Sales Deck', format: 'HTML', description: 'A presentation deck to pitch V2 Cloud solutions' },
  customer_overview: { label: 'Customer Overview', format: 'HTML', description: 'AI-researched overview with pain points and approach strategy', badge: 'Web Research' },
  customer_status: { label: 'Customer Status Report', format: 'HTML', description: 'HubSpot-powered analysis of interactions and next steps', badge: 'HubSpot' },
  customer_one_pager: { label: 'Customer One-Pager', format: 'PDF', description: 'Client-facing PDF one-pager with research and value props', badge: 'Web Research' },
};

const DEFAULT_INDUSTRY_OPTIONS = [
  'Accounting & Finance', 'Architecture & Engineering', 'Construction',
  'Education', 'Government', 'Healthcare', 'Insurance', 'Legal',
  'Manufacturing', 'Media & Entertainment', 'Non-Profit', 'Real Estate',
  'Retail & E-Commerce', 'Software & Technology', 'Staffing & Recruiting',
  'Telecommunications', 'Transportation & Logistics', 'Other'
];

const COMPANY_SIZE_OPTIONS = [
  '1-10 employees', '11-50 employees', '51-200 employees',
  '201-500 employees', '501-1000 employees', '1000+ employees'
];

const TIMELINE_OPTIONS = [
  'Immediate (within 2 weeks)', '1-3 months', '3-6 months',
  '6-12 months', 'Exploring / No timeline'
];

const PROGRESS_MESSAGES = [
  'Analyzing client profile...',
  'Researching industry context...',
  'Tailoring content to prospect...',
  'Building value propositions...',
  'Formatting the document...',
  'Running quality checks...',
  'Almost there, final polish...',
];

export default function SalesAssetGenerator() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');

  // Client context fields
  const [clientContext, setClientContext] = useState({
    companyName: '',
    clientName: '',
    clientTitle: '',
    clientEmail: '',
    industry: '',
    companySize: '',
    website: '',
    location: '',
    useCase: '',
    painPoints: '',
    currentSolution: '',
    productsInterested: '',
    numberOfUsers: '',
    budget: '',
    agreedPrice: '',
    dealTimeline: '',
    competitors: '',
    complianceRequirements: '',
    technicalRequirements: '',
    additionalNotes: '',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [industryOptions, setIndustryOptions] = useState(DEFAULT_INDUSTRY_OPTIONS);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progressMsgIdx, setProgressMsgIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const { addToast } = useToast();

  const updateField = (field, value) => {
    setClientContext(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    // Fetch HubSpot industries on mount
    api.get('/sales/hubspot-industries').then(({ data }) => {
      if (data?.length) {
        setIndustryOptions(data.map(i => i.label || i.value));
      }
    }).catch(() => {}); // Silently fall back to defaults
  }, []);

  useEffect(() => {
    if (selectedType) {
      api.get(`/topics?dashboardType=sales&assetType=${selectedType}`).then(({ data }) => setTopics(data)).catch(console.error);
    }
  }, [selectedType]);

  useEffect(() => {
    if (!jobId || !generating) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/sales/assets/${jobId}/status`);
        if (data.status === 'completed') { setGenerating(false); setResult(data); clearInterval(interval); }
        else if (data.status === 'failed') { setGenerating(false); setError('Generation failed. Please try again.'); clearInterval(interval); }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId, generating]);

  useEffect(() => {
    if (!generating) return;
    const interval = setInterval(() => { setProgressMsgIdx(prev => (prev + 1) % PROGRESS_MESSAGES.length); }, 8000);
    return () => clearInterval(interval);
  }, [generating]);

  const handleGenerate = async () => {
    if (!clientContext.companyName.trim()) {
      setError('Company name is required');
      return;
    }
    setError('');
    setGenerating(true);
    setProgressMsgIdx(0);
    setResult(null);
    try {
      const { data } = await api.post('/sales/assets/generate', {
        assetType: selectedType,
        topicId: selectedTopic,
        clientContext,
      });
      setJobId(data.id);
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.error || 'Failed to start generation');
    }
  };

  const handleRegenerate = async () => {
    if (!feedbackText.trim()) return;
    setShowFeedback(false);
    setGenerating(true);
    setProgressMsgIdx(0);
    setResult(null);
    setError('');
    try {
      const { data } = await api.post(`/sales/assets/${jobId}/regenerate`, { feedback: feedbackText });
      setJobId(data.id);
      setFeedbackText('');
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.error || 'Failed to regenerate');
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedType('');
    setSelectedTopic('');
    setClientContext({
      companyName: '', clientName: '', clientTitle: '', clientEmail: '',
      industry: '', companySize: '', website: '', location: '',
      useCase: '', painPoints: '', currentSolution: '', productsInterested: '',
      numberOfUsers: '', budget: '', agreedPrice: '', dealTimeline: '',
      competitors: '', complianceRequirements: '', technicalRequirements: '', additionalNotes: '',
    });
    setShowAdvanced(false);
    setJobId(null);
    setResult(null);
    setError('');
    setShowFeedback(false);
    setFeedbackText('');
  };

  // Step 1: Select type
  if (step === 1 && !generating && !result) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Generate Sales Asset</h1>
        <p className="text-gray-500 mb-8">Select the type of sales asset you want to create.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          {Object.entries(SALES_ASSET_TYPES).map(([type, config]) => (
            <button
              key={type}
              onClick={() => { setSelectedType(type); setStep(2); }}
              className="p-6 rounded-xl border-2 text-left transition-all hover:shadow-md border-gray-200 bg-white hover:border-v2-blue"
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900 text-lg">{config.label}</span>
                {config.badge && <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700 font-medium">{config.badge}</span>}
              </div>
              <div className="text-sm text-gray-500 mt-1">{config.description}</div>
              <div className="text-xs text-gray-400 mt-2">Output: {config.format}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Step 2: Client context + topic + generate
  if (step === 2 && !generating && !result) {
    const filledFields = Object.values(clientContext).filter(v => v.trim()).length;
    const totalFields = Object.keys(clientContext).length;

    return (
      <div className="max-w-4xl">
        <button onClick={() => setStep(1)} className="text-v2-blue hover:underline text-sm mb-4 inline-block">&larr; Back to asset types</button>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Generate {SALES_ASSET_TYPES[selectedType]?.label}
        </h1>
        <p className="text-gray-500 mb-6">Fill in the prospect details to generate a tailored asset. More context = better output.</p>

        {/* Completeness indicator */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Context completeness</span>
            <span className="text-sm text-gray-500">{filledFields}/{totalFields} fields</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all ${filledFields >= 8 ? 'bg-green-500' : filledFields >= 4 ? 'bg-yellow-500' : 'bg-red-400'}`}
              style={{ width: `${(filledFields / totalFields) * 100}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {filledFields < 4 ? 'Add more details for a better result' : filledFields < 8 ? 'Good start, more details will improve quality' : 'Great level of detail!'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column: Client info */}
          <div className="space-y-4">
            {/* HubSpot Import */}
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-orange-800 mb-1">Import from HubSpot</h3>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="Enter contact email..."
                      value={clientContext.clientEmail}
                      onChange={e => updateField('clientEmail', e.target.value)}
                      className="flex-1 px-3 py-1.5 text-sm border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                    />
                    <button
                      onClick={async () => {
                        if (!clientContext.clientEmail) return;
                        try {
                          const { data } = await api.post('/sales/import-hubspot', { email: clientContext.clientEmail });
                          if (data.clientContext) {
                            setClientContext(prev => {
                              const updated = { ...prev };
                              Object.entries(data.clientContext).forEach(([k, v]) => {
                                if (v && !updated[k]) updated[k] = v;
                              });
                              return updated;
                            });
                            addToast('Client data imported from HubSpot');
                          }
                        } catch (err) {
                          addToast(err.response?.data?.error || 'HubSpot import failed', 'error');
                        }
                      }}
                      className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                    >
                      Import
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Company & Contact */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                Company & Contact
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name <span className="text-red-500">*</span></label>
                  <input type="text" value={clientContext.companyName} onChange={e => updateField('companyName', e.target.value)}
                    placeholder="e.g., Acme Corporation" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                    <input type="text" value={clientContext.clientName} onChange={e => updateField('clientName', e.target.value)}
                      placeholder="John Smith" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title / Role</label>
                    <input type="text" value={clientContext.clientTitle} onChange={e => updateField('clientTitle', e.target.value)}
                      placeholder="IT Director" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input type="email" value={clientContext.clientEmail} onChange={e => updateField('clientEmail', e.target.value)}
                    placeholder="john@acme.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                    <select value={clientContext.industry} onChange={e => updateField('industry', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent bg-white">
                      <option value="">Select industry</option>
                      {industryOptions.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Size</label>
                    <select value={clientContext.companySize} onChange={e => updateField('companySize', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent bg-white">
                      <option value="">Select size</option>
                      {COMPANY_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input type="url" value={clientContext.website} onChange={e => updateField('website', e.target.value)}
                      placeholder="https://acme.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input type="text" value={clientContext.location} onChange={e => updateField('location', e.target.value)}
                      placeholder="New York, NY" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                </div>
              </div>
            </div>

            {/* Needs & Pain Points */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                Needs & Pain Points
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Use Case</label>
                  <textarea value={clientContext.useCase} onChange={e => updateField('useCase', e.target.value)} rows={2}
                    placeholder="e.g., They need to enable 50 remote workers with secure cloud desktops for CAD/CAM applications"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pain Points / Challenges</label>
                  <textarea value={clientContext.painPoints} onChange={e => updateField('painPoints', e.target.value)} rows={2}
                    placeholder="e.g., High hardware costs, security concerns with BYOD, difficulty managing remote IT infrastructure"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Solution / Infrastructure</label>
                  <textarea value={clientContext.currentSolution} onChange={e => updateField('currentSolution', e.target.value)} rows={2}
                    placeholder="e.g., On-prem servers with Citrix, aging hardware fleet, local Active Directory"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Deal details + topic */}
          <div className="space-y-4">
            {/* Products & Deal */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Products & Deal Details
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Products / Solutions Interested In</label>
                  <textarea value={clientContext.productsInterested} onChange={e => updateField('productsInterested', e.target.value)} rows={2}
                    placeholder="e.g., Cloud Desktops, GPU-powered workstations, Application Hosting"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Users</label>
                    <input type="text" value={clientContext.numberOfUsers} onChange={e => updateField('numberOfUsers', e.target.value)}
                      placeholder="e.g., 50" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Decision Timeline</label>
                    <select value={clientContext.dealTimeline} onChange={e => updateField('dealTimeline', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent bg-white">
                      <option value="">Select timeline</option>
                      {TIMELINE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget Range</label>
                    <input type="text" value={clientContext.budget} onChange={e => updateField('budget', e.target.value)}
                      placeholder="e.g., $5,000-$10,000/mo" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agreed / Quoted Price</label>
                    <input type="text" value={clientContext.agreedPrice} onChange={e => updateField('agreedPrice', e.target.value)}
                      placeholder="e.g., $7,500/mo" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Competitors Being Considered</label>
                  <input type="text" value={clientContext.competitors} onChange={e => updateField('competitors', e.target.value)}
                    placeholder="e.g., Citrix, AWS WorkSpaces, Azure Virtual Desktop"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent" />
                </div>
              </div>
            </div>

            {/* Advanced fields (collapsible) */}
            <div className="bg-white rounded-lg border border-gray-200">
              <button onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full px-6 py-4 flex items-center justify-between text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-lg">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  Advanced Details
                </span>
                {showAdvanced ? <HiOutlineChevronUp className="w-4 h-4" /> : <HiOutlineChevronDown className="w-4 h-4" />}
              </button>
              {showAdvanced && (
                <div className="px-6 pb-6 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Compliance / Regulatory Requirements</label>
                    <textarea value={clientContext.complianceRequirements} onChange={e => updateField('complianceRequirements', e.target.value)} rows={2}
                      placeholder="e.g., HIPAA, SOC 2, GDPR, PCI-DSS"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Technical Requirements</label>
                    <textarea value={clientContext.technicalRequirements} onChange={e => updateField('technicalRequirements', e.target.value)} rows={2}
                      placeholder="e.g., SSO integration with Okta, GPU support for 3D rendering, 99.9% uptime SLA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                    <textarea value={clientContext.additionalNotes} onChange={e => updateField('additionalNotes', e.target.value)} rows={3}
                      placeholder="Any other context that would help generate a better asset..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent resize-vertical" />
                  </div>
                </div>
              )}
            </div>

            {/* Topic selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-v2-blue" />
                Topic
              </h3>
              {topics.length === 0 ? (
                <p className="text-gray-400 text-sm">No topics available for this asset type.</p>
              ) : (
                <div className="space-y-2">
                  {topics.map(topic => (
                    <label key={topic.id} className={`block p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTopic === topic.id ? 'border-v2-blue bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        <input type="radio" name="topic" value={topic.id} checked={selectedTopic === topic.id}
                          onChange={() => setSelectedTopic(topic.id)} className="mt-0.5" />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{topic.title}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{topic.description}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error + Generate button */}
        <div className="mt-6">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}

          <button onClick={handleGenerate} disabled={!selectedTopic || !clientContext.companyName.trim()}
            className="w-full bg-v2-blue text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg">
            Generate {SALES_ASSET_TYPES[selectedType]?.label} for {clientContext.companyName || '...'}
          </button>
          <p className="text-xs text-gray-400 text-center mt-2">The more details you provide, the more personalized the output will be.</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (generating) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="mb-8">
          <div className="w-16 h-16 border-4 border-v2-blue border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Generating {SALES_ASSET_TYPES[selectedType]?.label}
          {clientContext.companyName && <span className="text-v2-blue"> for {clientContext.companyName}</span>}
        </h2>
        <p className="text-v2-blue font-medium mb-2 flex items-center justify-center gap-2">
          <span className="w-2 h-2 bg-v2-blue rounded-full animate-pulse" />
          {PROGRESS_MESSAGES[progressMsgIdx]}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
          <div className="h-2 bg-v2-blue rounded-full animate-progress" />
        </div>
        <p className="text-sm text-gray-400">This typically takes 1-2 minutes. Please don't refresh the page.</p>
      </div>
    );
  }

  // Result
  if (result) {
    const previewUrl = `${API_HOST}/api/sales/assets/${result.id}/preview`;
    const downloadUrl = `${API_HOST}/api/sales/assets/${result.id}/download`;
    const canPreview = ['html', 'docx', 'pdf'].includes(result.fileFormat);

    return (
      <div className="max-w-5xl mx-auto">
        {/* Preview area */}
        {result.filePath && canPreview && (
          <div className="bg-white rounded-xl border border-gray-200 mb-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <span className="text-sm font-medium text-gray-700">Preview</span>
              <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs text-v2-blue hover:underline flex items-center gap-1">
                <HiOutlineEye className="w-3.5 h-3.5" /> Open full preview
              </a>
            </div>
            <div className="h-[500px]">
              <iframe src={previewUrl} className="w-full h-full border-0" title="Asset Preview" sandbox="allow-same-origin" />
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Asset Generated Successfully!</h2>
              <p className="text-sm text-gray-500">
                Your {SALES_ASSET_TYPES[selectedType]?.label}
                {clientContext.companyName && <> for <strong>{clientContext.companyName}</strong></>}
                {' '}is ready.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {result.filePath && (
              <a href={downloadUrl}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700">
                <HiOutlineDownload className="w-5 h-5" />
                Download {result.fileFormat?.toUpperCase()}
              </a>
            )}
            <button onClick={() => setShowFeedback(true)} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">Request Changes</button>
            <button onClick={reset} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">Generate Another</button>
          </div>

          {showFeedback && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Describe the changes you'd like:</label>
              <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
                placeholder="e.g., Emphasize cost savings more, add ROI calculations, adjust the pricing section..." />
              <button onClick={handleRegenerate} disabled={!feedbackText.trim()}
                className="mt-3 px-6 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                Regenerate with Changes
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
